import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface RefundRequestApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  request: any;
  onApprove: (requestId: number, reviewNotes: string) => void;
}

const RefundRequestApprovalDialog: React.FC<RefundRequestApprovalDialogProps> = ({
  open,
  onClose,
  request,
  onApprove
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [reviewNotes, setReviewNotes] = useState('');

  const handleClose = () => {
    setActiveStep(0);
    setReviewNotes('');
    onClose();
  };

  const handleContinue = () => {
    setActiveStep(1);
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleConfirm = () => {
    onApprove(request.id, reviewNotes);
    handleClose();
  };

  if (!request) return null;

  const steps = ['Review Details', 'Confirm Approval'];

  const getItemTypeName = (type: string) => {
    const typeMap: { [key: string]: string } = {
      adSpace: 'Ad Space',
      tokenPlan: 'Token Plan',
      subscriptionPlan: 'Subscription Plan',
      purchasePlan: 'Purchase Plan'
    };
    return typeMap[type] || type;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Approve Refund Request - ID #{request.id}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Review the refund request details before approving.
            </Alert>

            <Box>
              <Typography variant="h6" gutterBottom>Request Summary</Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><strong>User</strong></TableCell>
                    <TableCell>{request.user?.name || request.user?.email || `ID: ${request.userId}`}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Item Type</strong></TableCell>
                    <TableCell>{getItemTypeName(request.itemType)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Item Name</strong></TableCell>
                    <TableCell>{request.itemDetails?.itemName || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Refund Amount</strong></TableCell>
                    <TableCell sx={{ fontSize: '1.1em', fontWeight: 'bold', color: 'error.main' }}>
                      ${parseFloat(request.refundAmount).toFixed(2)} {request.itemDetails?.currencyCode || 'USD'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Payment Intent</strong></TableCell>
                    <TableCell>{request.paymentIntentId}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Reason</strong></TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                      {request.reason || 'No reason provided'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Requested On</strong></TableCell>
                    <TableCell>{new Date(request.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Review Notes (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Add any notes about this approval (optional)"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                variant="outlined"
              />
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                ⚠️ Warning: This will process a Stripe refund immediately!
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Once confirmed, this action cannot be undone. The refund will be processed through Stripe
                and the user will be notified via email.
              </Typography>
            </Alert>

            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>Confirmation Summary</Typography>
              <Typography variant="body2">
                <strong>User:</strong> {request.user?.name || request.user?.email}
              </Typography>
              <Typography variant="body2">
                <strong>Item:</strong> {request.itemDetails?.itemName}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '1.1em', color: 'error.main', mt: 1 }}>
                <strong>Refund Amount:</strong> ${parseFloat(request.refundAmount).toFixed(2)} {request.itemDetails?.currencyCode || 'USD'}
              </Typography>
              {reviewNotes && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Your Notes:</strong> {reviewNotes}
                </Typography>
              )}
            </Box>

            <Alert severity="info">
              After approval, the system will:
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Update the request status to "approved"</li>
                <li>Process the Stripe refund automatically</li>
                <li>Send a confirmation email to the user</li>
                <li>Update the status to "processed" on success</li>
              </ul>
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        {activeStep === 1 && (
          <Button onClick={handleBack} color="inherit">
            Back
          </Button>
        )}
        {activeStep === 0 ? (
          <Button onClick={handleContinue} variant="contained" color="primary">
            Continue to Approve
          </Button>
        ) : (
          <Button onClick={handleConfirm} variant="contained" color="error">
            Confirm Approval
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RefundRequestApprovalDialog;
